import {
  getStartOf,
  getStartOfWeek,
  isSameDay,
  parseDateFromISO8601,
  parseDateTimeString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { availabilityPerDate } from '../../util/generators';

import {
  bookingEnd,
  bookingStart,
  processInfo,
} from '../InstructorDashboardPage/InstructorDashboardPage.helpers';

/**
 * The diary is one Monday-to-Sunday week, read-only. Everything here is pure: it turns the
 * listing's weekly hours, its availability exceptions (blocks the instructor added and busy
 * times calendar sync added) and the week's bookings into one list of entries per day.
 *
 * Every time is placed in the listing's time zone, not the browser's, so an instructor who
 * travels interstate still sees their Melbourne week.
 */

const MONDAY = 1;
const MINUTE_MS = 60 * 1000;

/** How far back and ahead the diary goes, in weeks. */
export const DIARY_WEEKS_EACH_WAY = 26;

/** Gaps shorter than this are dropped; they are rounding, not time anyone could use. */
const MIN_GAP_MINUTES = 15;

/** The default shortest lesson, used to mark a gap "too short for a lesson". */
export const DEFAULT_MIN_LESSON_MINUTES = 60;

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/**
 * The weekly hours the instructor set for one day, as Dates. "00:00" as an end time means
 * midnight at the end of the day.
 */
const weeklyHours = (plan, dayIndex, dayId, date, timeZone) =>
  (plan?.entries || [])
    .filter(e => e.dayOfWeek === WEEKDAY_KEYS[dayIndex] && e.seats > 0)
    .map(e => ({
      start: parseDateTimeString(`${dayId} ${e.startTime}`, timeZone),
      end:
        e.endTime === '00:00'
          ? getStartOf(date, 'day', timeZone, 1, 'days')
          : parseDateTimeString(`${dayId} ${e.endTime}`, timeZone),
    }))
    .sort((a, b) => a.start - b.start);

const minutesBetween = (start, end) => Math.round((end.getTime() - start.getTime()) / MINUTE_MS);

/**
 * Monday of the week to show. `dateString` is the page's ?date= (YYYY-MM-DD); anything missing,
 * malformed or further than DIARY_WEEKS_EACH_WAY weeks from this week falls back to this week.
 *
 * @param {string?} dateString
 * @param {string} timeZone
 * @param {Date} now
 * @returns {Date} start of the Monday, in timeZone
 */
export const diaryWeekStart = (dateString, timeZone, now = new Date()) => {
  const thisWeek = getStartOfWeek(now, timeZone, MONDAY);
  const isWellFormed = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isWellFormed ? parseDateFromISO8601(dateString, timeZone) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return thisWeek;
  }
  const requested = getStartOfWeek(parsed, timeZone, MONDAY);
  const earliest = getStartOf(thisWeek, 'day', timeZone, -7 * DIARY_WEEKS_EACH_WAY, 'days');
  const latest = getStartOf(thisWeek, 'day', timeZone, 7 * DIARY_WEEKS_EACH_WAY, 'days');
  return requested < earliest || requested > latest ? thisWeek : requested;
};

/**
 * The Mondays either side of `weekStart`, or null where the diary stops.
 */
export const neighbourWeeks = (weekStart, timeZone, now = new Date()) => {
  const thisWeek = getStartOfWeek(now, timeZone, MONDAY);
  const shift = weeks => getStartOf(weekStart, 'day', timeZone, 7 * weeks, 'days');
  const weeksFromThisWeek = Math.round(
    (getStartOfWeek(shift(0), timeZone, MONDAY).getTime() - thisWeek.getTime()) /
      (7 * 24 * 60 * MINUTE_MS)
  );
  return {
    previous: weeksFromThisWeek > -DIARY_WEEKS_EACH_WAY ? shift(-1) : null,
    next: weeksFromThisWeek < DIARY_WEEKS_EACH_WAY ? shift(1) : null,
    isThisWeek: weekStart.getTime() === thisWeek.getTime(),
    thisWeek,
  };
};

/**
 * The diary's status for a booking, or null for bookings it doesn't show (declined, expired,
 * never paid). Requests still hold their time, so they are shown and they close a gap.
 */
export const lessonStatus = tx => {
  const { process, state } = processInfo(tx);
  if (!process || !state) {
    return null;
  }
  const s = process.states || {};
  if (process.statesNeedingProviderAttention?.includes(state)) {
    return 'waiting';
  }
  if (state === s.ACCEPTED) {
    return 'booked';
  }
  if (
    [s.DELIVERED, s.REVIEWED, s.REVIEWED_BY_CUSTOMER, s.REVIEWED_BY_PROVIDER]
      .filter(Boolean)
      .includes(state)
  ) {
    return 'completed';
  }
  if (state === s.CANCELED) {
    return 'cancelled';
  }
  return null;
};

const takesTime = entry => entry.kind === 'lesson' && entry.status !== 'cancelled';

/**
 * `intervals` minus `cuts`, both arrays of { start, end } Dates.
 */
const subtract = (intervals, cuts) =>
  intervals.flatMap(interval => {
    let pieces = [interval];
    cuts.forEach(cut => {
      pieces = pieces.flatMap(p => {
        if (cut.end <= p.start || cut.start >= p.end) {
          return [p];
        }
        const out = [];
        if (cut.start > p.start) out.push({ start: p.start, end: cut.start });
        if (cut.end < p.end) out.push({ start: cut.end, end: p.end });
        return out;
      });
    });
    return pieces;
  });

const addMoney = (total, money) =>
  money
    ? { amount: total.amount + money.amount, currency: money.currency || total.currency }
    : total;

/**
 * One week of diary entries.
 *
 * Entry kinds:
 * - lesson:  { status: 'waiting' | 'booked' | 'completed' | 'cancelled', tx }
 * - synced:  busy time calendar sync blocked, with `provider` ('outlook' | 'google' | null)
 * - blocked: time the instructor blocked themselves on the availability page
 * - open:    bookable time no one has booked, with `minutes` and `tooShort`; only from now on
 *
 * @param {Object} params
 * @param {Object} params.plan listing availabilityPlan ({ timezone, entries })
 * @param {Array} params.exceptions availability exceptions overlapping the week
 * @param {Set<string>} params.calendarBlockIds exception ids in listing privateData.calendarBlocks
 * @param {string?} params.calendarProvider listing privateData.calendarProvider
 * @param {Array} params.transactions the instructor's sales, with booking
 * @param {Date} params.weekStart Monday, start of day in timeZone
 * @param {string} params.timeZone
 * @param {Date} params.now
 * @param {number} params.minLessonMinutes
 */
export const buildDiaryWeek = ({
  plan,
  exceptions = [],
  calendarBlockIds = new Set(),
  calendarProvider = null,
  transactions = [],
  weekStart,
  timeZone,
  now = new Date(),
  minLessonMinutes = DEFAULT_MIN_LESSON_MINUTES,
}) => {
  const nextWeekStart = getStartOf(weekStart, 'day', timeZone, 7, 'days');
  // availabilityPerDate reads the plan's own time zone; the diary always passes one.
  const perDate = plan
    ? availabilityPerDate(weekStart, nextWeekStart, { ...plan, timezone: timeZone }, exceptions)
    : {};

  const lessons = transactions
    .map(tx => ({ tx, status: lessonStatus(tx), start: bookingStart(tx), end: bookingEnd(tx) }))
    .filter(l => l.status && l.start && l.end)
    .map(l => ({ kind: 'lesson', id: l.tx.id.uuid, ...l }));

  const days = Array.from({ length: 7 }, (_, i) => {
    const date = getStartOf(weekStart, 'day', timeZone, i, 'days');
    const id = stringifyDateToISO8601(date, timeZone);
    const ranges = perDate[id]?.ranges || [];

    const dayLessons = lessons.filter(l => isSameDay(l.start, date, timeZone));

    const blocks = ranges
      .filter(r => r.exception && !(r.seats > 0))
      .map(r => {
        const exceptionId = r.exception.id.uuid;
        const isSynced = calendarBlockIds.has(exceptionId);
        return {
          kind: isSynced ? 'synced' : 'blocked',
          id: `${exceptionId}-${id}`,
          exceptionId,
          start: r.start,
          end: r.end,
          ...(isSynced ? { provider: calendarProvider || null } : {}),
        };
      });

    const bookable = ranges.filter(r => r.seats > 0).map(r => ({ start: r.start, end: r.end }));
    const open = subtract(bookable, dayLessons.filter(takesTime))
      // Open time in the past can't be booked any more.
      .map(g => (g.start < now ? { start: now, end: g.end } : g))
      .filter(g => minutesBetween(g.start, g.end) >= MIN_GAP_MINUTES)
      .map(g => {
        const minutes = minutesBetween(g.start, g.end);
        return {
          kind: 'open',
          id: `open-${g.start.getTime()}`,
          start: g.start,
          end: g.end,
          minutes,
          tooShort: minutes < minLessonMinutes,
        };
      });

    const entries = [...dayLessons, ...blocks, ...open].sort(
      (a, b) => a.start - b.start || (a.kind === 'lesson' ? -1 : b.kind === 'lesson' ? 1 : 0)
    );

    return {
      id,
      date,
      isToday: isSameDay(date, now, timeZone),
      hours: weeklyHours(plan, i, id, date, timeZone),
      entries,
    };
  });

  const weekLessons = days.flatMap(d => d.entries).filter(e => e.kind === 'lesson');
  const paid = weekLessons.filter(l => l.status === 'booked' || l.status === 'completed');
  const totals = {
    lessonCount: paid.length,
    requestCount: weekLessons.filter(l => l.status === 'waiting').length,
    bookedMinutes: paid.reduce((sum, l) => sum + minutesBetween(l.start, l.end), 0),
    openMinutes: days
      .flatMap(d => d.entries)
      .filter(e => e.kind === 'open')
      .reduce((sum, g) => sum + g.minutes, 0),
    earnings: paid.reduce((sum, l) => addMoney(sum, l.tx.attributes?.payoutTotal), {
      amount: 0,
      currency: null,
    }),
  };

  return { days, totals };
};
